import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: MemoryInfo;
}

// Dynamic check intervals based on memory pressure
const MEMORY_CHECK_INTERVAL_NORMAL = 60000;   // 60 seconds when memory is low
const MEMORY_CHECK_INTERVAL_ELEVATED = 30000; // 30 seconds when cleanup starts
const MEMORY_CHECK_INTERVAL_HIGH = 15000;     // 15 seconds when warning
const MEMORY_CHECK_INTERVAL_CRITICAL = 10000; // 10 seconds when critical

// Memory thresholds
const CACHE_CLEANUP_THRESHOLD = 0.5;    // 50% - start cleanup earlier
const MEMORY_WARNING_THRESHOLD = 0.7;   // 70% of heap limit
const MEMORY_CRITICAL_THRESHOLD = 0.85; // 85% of heap limit

/**
 * Memory watchdog hook that monitors memory usage and triggers cleanup
 */
export const useMemoryWatchdog = () => {
  const queryClient = useQueryClient();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasWarnedRef = useRef(false);
  const [memoryUsage, setMemoryUsage] = useState<number | null>(null);

  const getMemoryUsage = useCallback((): number | null => {
    const perf = performance as PerformanceWithMemory;
    if (!perf.memory) return null;
    
    const { usedJSHeapSize, jsHeapSizeLimit } = perf.memory;
    return usedJSHeapSize / jsHeapSizeLimit;
  }, []);

  // Get appropriate check interval based on current memory usage
  const getCheckInterval = useCallback((usage: number | null): number => {
    if (usage === null) return MEMORY_CHECK_INTERVAL_NORMAL;
    
    if (usage >= MEMORY_CRITICAL_THRESHOLD) return MEMORY_CHECK_INTERVAL_CRITICAL;
    if (usage >= MEMORY_WARNING_THRESHOLD) return MEMORY_CHECK_INTERVAL_HIGH;
    if (usage >= CACHE_CLEANUP_THRESHOLD) return MEMORY_CHECK_INTERVAL_ELEVATED;
    return MEMORY_CHECK_INTERVAL_NORMAL;
  }, []);

  // Standard cleanup - removes stale and inactive queries
  const cleanupCache = useCallback(() => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    let removedCount = 0;
    const now = Date.now();
    const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    
    queries.forEach(query => {
      const state = query.state;
      const isStale = state.dataUpdatedAt && (now - state.dataUpdatedAt > STALE_THRESHOLD);
      const isInactive = query.getObserversCount() === 0;
      
      // Remove queries that are both stale and inactive
      if (isStale && isInactive) {
        cache.remove(query);
        removedCount++;
      }
    });
    
    // Clear old localStorage items
    try {
      const keys = Object.keys(localStorage);
      keys.filter(k => 
        k.startsWith('visual-generation-results-') ||
        k.startsWith('temp-') ||
        k.startsWith('draft-')
      ).slice(0, 10).forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.warn('[MemoryWatchdog] Failed to clean localStorage:', e);
    }
    
    return removedCount;
  }, [queryClient]);

  // Aggressive cleanup - removes ALL inactive queries regardless of staleness
  const aggressiveCleanup = useCallback(() => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    let removedCount = 0;
    
    queries.forEach(query => {
      const isInactive = query.getObserversCount() === 0;
      
      // Remove ALL inactive queries when memory is high
      if (isInactive) {
        cache.remove(query);
        removedCount++;
      }
    });
    
    // More aggressive localStorage cleanup
    try {
      const keys = Object.keys(localStorage);
      const patternsToClean = [
        'visual-generation-results-',
        'temp-',
        'draft-',
        'notification-',
        'cache-',
        'dismissed-'
      ];
      
      keys.filter(k => patternsToClean.some(p => k.startsWith(p)))
        .forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.warn('[MemoryWatchdog] Failed to clean localStorage:', e);
    }
    
    console.log(`[MemoryWatchdog] Aggressive cleanup removed ${removedCount} queries`);
    return removedCount;
  }, [queryClient]);

  const checkMemory = useCallback(() => {
    const usage = getMemoryUsage();
    if (usage === null) return;
    
    setMemoryUsage(usage);
    
    // Determine new interval based on current usage
    const newInterval = getCheckInterval(usage);
    
    // Update interval if it changed
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(checkMemory, newInterval);
    }
    
    // Standard cleanup at 50% usage
    if (usage >= CACHE_CLEANUP_THRESHOLD && usage < MEMORY_WARNING_THRESHOLD) {
      cleanupCache();
    }
    
    // Aggressive cleanup at 70%+
    if (usage >= MEMORY_WARNING_THRESHOLD) {
      aggressiveCleanup();
      
      if (!hasWarnedRef.current) {
        hasWarnedRef.current = true;
        console.warn(`[MemoryWatchdog] High memory usage: ${(usage * 100).toFixed(1)}%`);
      }
    }
    
    // Critical at 85% - suggest reload
    if (usage >= MEMORY_CRITICAL_THRESHOLD) {
      console.error(`[MemoryWatchdog] Critical memory usage: ${(usage * 100).toFixed(1)}%`);
      toast.warning('High memory usage detected. Consider refreshing the page for better performance.', {
        duration: 10000,
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload(),
        },
      });
    }
  }, [getMemoryUsage, getCheckInterval, cleanupCache, aggressiveCleanup]);

  // Force cleanup exposed for manual use
  const forceCleanup = useCallback(() => {
    const removed = aggressiveCleanup();
    
    // Also trigger garbage collection hint
    if ('gc' in window && typeof (window as any).gc === 'function') {
      try {
        (window as any).gc();
      } catch (e) {
        // gc not available in production
      }
    }
    
    return removed;
  }, [aggressiveCleanup]);

  useEffect(() => {
    // Initial check
    checkMemory();
    
    // Set up initial interval
    const initialInterval = getCheckInterval(getMemoryUsage());
    intervalRef.current = setInterval(checkMemory, initialInterval);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkMemory, getCheckInterval, getMemoryUsage]);

  return { memoryUsage, forceCleanup };
};
